import { AnalysisMetricTypes, BaseRateTypes, Guid } from '../../models';

export * from './useDealQuery';
export * from './useIntegrationConfigQuery';
export * from './useDealStatusViewQuery';
export * from './useSubscriptionSuccessFeeTiersQuery';
export * from './useDealStatusHistoryQuery';
export * from './useDealGeographiesQuery';
export * from './useExcludedLendersQuery';
export * from './useReducedFeeLendersQuery';
export * from './useActiveLenderQuery';
export * from './useFinderFeeAgreementsQuery';
export * from './useDealInvoicesQuery';
export * from './useDealProductSubscriptionsQuery';
export * from './useCompanySubscriptionSuccessFeeTiersQuery';
export * from './useCreditExplorerProductsQuery';
export * from './useComplianceNavigatorProductsQuery';
export * from './useActiveTermSheetsQuery';
export * from './useDataRoomTermSheetsQuery';
export * from './useGetTermSheetQuery';
export * from './useGetTermSheetRevisionQuery';
export * from './useFinancialCovenantTypesQuery';
export * from './useTermSheetFacilityCollateralQuery';
export * from './useTermSheetFacilityFeesQuery';
export * from './useGetHistoricalBankRateQuery';
export * from './useDataRoomFolderHierarchyQuery';
export * from './useStatesQuery';
export * from './useIndustriesQuery';
export * from './useCompanyNdaQuery';
export * from './useDealRequestAllocationQuery';
export * from './useDealRequiredDocumentsQuery';
export * from './useRequiredDocumentTemplatesQuery';
export * from './useDealTypePreferenceQuery';
export * from './useDealMatchingTranchesQuery';
export * from './useDealMatchingStatsQuery';
export * from './useDealAllocationRealEstatesQuery';
export * from './useNaicsCodeQuery';
export * from './useTermSheetFeesQuery';
export * from './useCurrentUserQuery';
export * from './useDebtScheduleQuery';
export * from './useDealFinancialMetricsQuery';
export * from './useTransactionManagersQuery';
export * from './useSecondaryTransactionManagersQuery';
export * from './useLenderRelationsManagersQuery';
export * from './useDealPartnerQuery';
export * from './useDealPartnerContactQuery';
export * from './useDealQuestionsQuery';
export * from './useDealQuestionAnswersQuery';
export * from './useOutputAnalysisStructureQuery';
export * from './useDealProductQuery';
export * from './useCompanyContactsQuery';
export * from './useMatchingQuery';
export * from './useTermSheetRevisionMatchingParamsQuery';
export * from './useCurrentRatesQuery';
export * from './useIsMatchingProcessingQuery';
export * from './useMatchingTermSheetFacilitiesQuery';
export * from './useConditionTypesQuery';
export * from './useApplicationSettingsQuery';
export * from './useProductSubscriptionPaymentTermsQuery';
export * from './useCompanySubscriptionPaymentTermsQuery';
export * from './useDefaultProductQuery';
export * from './useDataRoomBorrowerParticipantsQuery';
export * from './useRelatedDealsQuery';
export * from './useSalesRepsQuery';
export * from './useMatchingUnavailableReasonsQuery';

export const QueryKeys = {
	CurrentUser: (authToken: string) => ['Current User', authToken],
	Users: {
		TransactionManagers: ['TransactionManagers'],
		SalesReps: ['SalesReps'],
		SecondaryTransactionManagers: ['SecondaryTransactionManagers'],
		LenderRelationsManagers: ['LenderRelationsManagers'],
	},
	Deal: {
		Root: (dealGuid: Guid | undefined) => ['Deal', dealGuid],
		Related: (dealGuid: Guid | undefined) => ['Related Deals', dealGuid],
		Nda: (dealGuid: Guid | undefined) => ['Deal Nda', dealGuid],
		Invoices: (dealGuid: Guid | undefined) => ['Deal Invoices', dealGuid],
		Subscriptions: (dealGuid: Guid | undefined) => ['Deal Subscriptions', dealGuid],
		UpdateStatus: (dealGuid: Guid | undefined) => ['Deal Status', dealGuid],
		SubscriptionTiers: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) => [
			'Subscription Success Fee Tiers',
			dealGuid,
			subscriptionGuid,
		],
		PaymentTerms: (dealGuid: Guid | undefined, subscriptionGuid: Guid | undefined) => [
			'Subscription Payment Terms',
			dealGuid,
			subscriptionGuid,
		],
	},
	DataRoom: {
		FolderHierarchy: (dataRoomGuid: Guid | undefined) => ['Data Room Folder Hierarchy', dataRoomGuid],
	},
	StatusHistory: (dealGuid: Guid | undefined) => ['Status History', dealGuid],

	ActiveNda: (companyGuid: Guid | undefined) => ['Active Company Nda', companyGuid],
	Company: {
		Root: (dealGuid: Guid | undefined) => ['Company', dealGuid],
		SubscriptionTiers: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) => [
			'Company Subscription Success Fee Tiers',
			companyGuid,
			subscriptionGuid,
		],
		PaymentTerms: (companyGuid: Guid | undefined, subscriptionGuid: Guid | undefined) => [
			'Company Subscription Payment Terms',
			companyGuid,
			subscriptionGuid,
		],
	},
	CompanyContacts: (companyGuid: Guid | undefined) => ['Company Contacts', companyGuid],
	DataRoomContacts: {
		Root: (dataRoomGuid: Guid | undefined) => ['Data Room Borrower Participants', dataRoomGuid],
	},
	Allocation: (dealGuid: Guid | undefined) => ['Deal Request Allocation', dealGuid],

	AllocationRealEstates: (dealGuid: Guid | undefined) => ['Deal Request Allocation Real Estates', dealGuid],
	ActiveLenders: (dealGuid: Guid | undefined) => ['Deal Active Lenders', dealGuid],
	FinderFees: {
		ByLender: (lenderGuid: Guid | undefined | null) => ['Finder Fees by Lender', lenderGuid],
	},
	TermSheets: {
		Specific: (dealGuid: Guid | undefined, termSheetGuid: Guid | null | undefined) => [
			'Term Sheet',
			dealGuid,
			termSheetGuid,
		],
		Revision: (
			dealGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => ['Term Sheet Revision', dealGuid, termSheetGuid, revisionGuid],
		RevisionMatchingParams: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => ['Term Sheet Revision Matching Params', dataRoomGuid, termSheetGuid, revisionGuid],
		Active: (dealGuid: Guid | undefined) => ['Active Term Sheets', dealGuid],
		ExistingTerms: (dealGuid: Guid | undefined) => ['Existing Terms', dealGuid],
		FacilityCollateral: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) => ['Term Sheet Facility Collateral', dataRoomGuid, termSheetGuid, revisionGuid, facilityGuid],
		FacilityFees: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) => ['Term Sheet Facility Fees', dataRoomGuid, termSheetGuid, revisionGuid, facilityGuid],
		HistoricalBankRate: (revisionGuid: Guid | null | undefined, rateType: BaseRateTypes | null | undefined) => [
			'Historical Bank Rate',
			revisionGuid,
			rateType,
		],
	},
	Geographies: (dealGuid: Guid | undefined, metricType: AnalysisMetricTypes) => [
		'Deal Geographies',
		dealGuid,
		metricType,
	],

	RequiredDocuments: {
		Root: (dealGuid: Guid | undefined) => ['Deal Required Documents', dealGuid],
		Templates: () => ['Required Document Templates'],
	},

	ExcludedLenders: (dealGuid: Guid | undefined) => ['Deal Excluded Lenders', dealGuid],
	ReducedFeeLenders: (dealGuid: Guid | undefined) => ['Deal Reduced Fee Lenders', dealGuid],

	DealPartners: {
		Search: (search?: string | null) => (search == null ? ['Deal Partners'] : ['Deal Partners', search]),
		Contacts: (dealPartnerGuid: Guid | undefined) => ['Deal Partner Contact', dealPartnerGuid],
	},

	DebtSchedule: (dealGuid: Guid | undefined) => ['Debt Schedule', dealGuid],

	Questions: {
		LoanApp: ['Questions'],
		Answers: (dealGuid: Guid | undefined) => ['Deal Question Answers', dealGuid],
	},

	FinancialMetrics: {
		All: (dealGuid: Guid | undefined, metricType: AnalysisMetricTypes) => [
			'Deal Financial Metrics',
			dealGuid,
			metricType,
		],

		Filtered: (dealGuid: Guid | undefined, metricType: AnalysisMetricTypes, metricTypesQueryString: string) => [
			'Deal Financial Metrics',
			dealGuid,
			metricType,
			metricTypesQueryString,
		],
	},

	Matching: {
		Get: (dealGuid: Guid | undefined) => ['Matching', dealGuid],
		Rates: () => ['CurrentRates'],
		Stats: (dealGuid: Guid | undefined) => ['Matching Stats', dealGuid],
		Tranches: (dealGuid: Guid | undefined) => ['Matching Tranches', dealGuid],
		Preferences: (dealGuid: Guid | undefined) => ['Matching Preferences', dealGuid],
		OutputAnalysis: (dealGuid: Guid | undefined) => ['Output Analysis', dealGuid],
		Params: (dealGuid: Guid | undefined) => ['Matching Params', dealGuid],
		Processing: (dealGuid: Guid | undefined) => ['Matching Processing', dealGuid],
		Lenders: (
			dealGuid?: string,
			mode?: number,
			tier1?: number,
			finderFee?: boolean,
			structure?: number | null,
			filterType?: number,
			matchMode?: number | null
		) => ['Matching Lenders', dealGuid, mode, tier1, finderFee, structure, filterType, matchMode],
		TermSheets: (dealGuid?: string, structure?: number | null, tier1?: number | null) => [
			'Matching Term Sheets',
			dealGuid,
			structure,
			tier1,
		],
		UnavailableReasons: (dealGuid?: string, structureTranche?: number | null, tier1BankType?: number | null) => [
			'Matching Tranche Reasons',
			dealGuid,
			structureTranche,
			tier1BankType,
		],
	},

	Lookups: {
		Industries: ['Industries'],
		FinancialCovenantTypes: ['Financial Covenant Types'],
		States: (search?: string | null) => (search == null ? ['States'] : ['States', search]),
		NaicsCodes: (search?: string | null) => (search == null ? ['Naics Codes'] : ['Naics Codes', search]),
		ExplorerProducts: () => ['CreditExplorerProducts'],
		NavigatorProducts: () => ['ComplianceNavigatorProducts'],
		TermSheetFees: ['Term Sheet Fees'],
		BankPartials: (search?: string | null, companyGuid?: Guid) => {
			const key = ['Bank Partials'];

			if (search) key.push(search);

			if (companyGuid) key.push(companyGuid);

			return key;
		},
		ConditionTypes: ['Condition Types'],
		ApplicationSettings: ['Application Settings'],
	},

	Product: (dealGuid: Guid | undefined) => ['Product', dealGuid],
	DefaultProduct: (dealGuid: Guid | undefined) => ['DefaultProduct', dealGuid],
	Integrations: {
		Config: () => ['Integration Config'],
	},
};
